import apiClient from './api';

// ─── Types ───────────────────────────────────────────────────────────────────
export interface AuditFinding {
  findingCode: string;
  severity: 'Critical' | 'Warning' | 'Info';
  title: string;
  explanation: string;
  evidence: string[];
  birthDate?: string | null;
  birthDocument?: string | null;
  birthUser?: string | null;
  canAutoFix: boolean;
  confidencePercent: number;
  hypothesis?: string | null;
  confirmationQuestion?: string | null;
}

export interface AuditTimelineEvent {
  date: string;
  documentType: string;
  documentNumber: string;
  description: string;
  quantity: number;
  unitName: string;
  unitCost: number;
  location: string;
  createdBy: string;
  isDefective: boolean;
}

export interface AuditStats {
  totalMovements: number;
  totalDocuments: number;
  relatedJournalEntries: number;
  locationsCount: number;
  currentTotalQuantityBase: number;
  currentTotalValue: number;
  firstMovementDate?: string | null;
  lastMovementDate?: string | null;
}

export interface ItemAuditResult {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  auditDate: string;
  healthScore: number;
  findings: AuditFinding[];
  timeline: AuditTimelineEvent[];
  narrativeReport: string;
  stats: AuditStats;
}

export interface FixChange {
  tableName: string;
  keyId: number;
  columnName: string;
  oldValue: number;
  newValue: number;
  documentRef: string;
  description: string;
}

export interface FixPlan {
  findingCode: string;
  itemId: number;
  summary: string;
  changes: FixChange[];
  report: string;
  confirmationQuestion?: string | null;
}

export interface FixStepResult {
  documentRef: string;
  description: string;
  success: boolean;
}

export interface FixExecutionResult {
  success: boolean;
  fixOperationId: number;
  message: string;
  executedSteps: FixStepResult[];
  verification: string;
  healthScoreAfter: number;
}

export interface FixHistoryEntry {
  fixOperationId: number;
  itemId: number;
  itemCode: string;
  itemName: string;
  findingCode: string;
  summary: string;
  executedBy: string;
  executedDate: string;
  changesCount: number;
  changes: FixChange[];
}

// ─── Service ─────────────────────────────────────────────────────────────────
export const itemAuditService = {
  audit: async (itemId: number, lang: string): Promise<ItemAuditResult> => {
    const response = await apiClient.get<ItemAuditResult>(`/ItemAudit/audit/${itemId}?lang=${lang}`);
    return response.data;
  },

  getFixPlan: async (itemId: number, findingCode: string, lang: string): Promise<FixPlan> => {
    const response = await apiClient.get<FixPlan>(`/ItemAudit/fix-plan/${itemId}/${findingCode}?lang=${lang}`);
    return response.data;
  },

  executeFix: async (itemId: number, findingCode: string, lang: string): Promise<FixExecutionResult> => {
    const response = await apiClient.post<FixExecutionResult>('/ItemAudit/execute-fix', { itemId, findingCode, lang });
    return response.data;
  },

  getHistory: async (itemId?: number): Promise<FixHistoryEntry[]> => {
    const response = await apiClient.get<FixHistoryEntry[]>(`/ItemAudit/history${itemId ? `?itemId=${itemId}` : ''}`);
    return response.data;
  },
};
